import React, { useCallback, useEffect, useState } from 'react';
import { Button, Layout, InlineStack, Text, TextField, Spinner, Modal, Tooltip, Icon, BlockStack, Select, Checkbox } from '@shopify/polaris';
import { AddMajor, MagicMinor, MaximizeMinor, ImportMinor, CancelSmallMinor, SaveMinor, IncomingMajor, ChatMajor, CircleInformationMajor, ExploreImagesMajor } from '@shopify/polaris-icons';
import AIDesignImaginePage from './AIDesignImaginePage';
import CreateSceneModal from './modals/CreateSceneModal';
import DeleteSceneModal from './modals/DeleteSceneModal';
import { authService } from '../services/authService';
import { getServerURL } from '../services/config';
import ConfirmAIStoryActionModal from './modals/ConfirmAIStoryActionModal';
import JSZip from 'jszip';

function AIStoryPage({ setToastMessage: setGlobalToastMessage, setToastActive: setGlobalToastActive }) {
  const [scenes, setScenes] = useState([]); // [{ id, title }]
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [activatedSceneIds, setActivatedSceneIds] = useState([]); // ids of scenes that have been mounted
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [fetchStoryName, setFetchStoryName] = useState('');
  const [isFetchingStoryScenes, setIsFetchingStoryScenes] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [clearAllOpen, setClearAllOpen] = useState(false);
  const [isClearingAll, setIsClearingAll] = useState(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [isExpandingAll, setIsExpandingAll] = useState(false);
  const [isUpscalingAll, setIsUpscalingAll] = useState(false);
  const [storySummary, setStorySummary] = useState('');

  // Tell Me A Story modal state
  const [tellStoryOpen, setTellStoryOpen] = useState(false);
  const [isGeneratingStoryScenes, setIsGeneratingStoryScenes] = useState(false);
  const [tellKidGender, setTellKidGender] = useState('');
  const [tellKidAge, setTellKidAge] = useState('');
  const [tellHero, setTellHero] = useState('');
  const [tellScenes, setTellScenes] = useState('8');
  const [tellStoryName, setTellStoryName] = useState('');
  const [tellKidName, setTellKidName] = useState('');
  const [tellAIGenerated, setTellAIGenerated] = useState(false);
  const [tellError, setTellError] = useState('');

  // Import story modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importStoryName, setImportStoryName] = useState('');
  const [isImportingStory, setIsImportingStory] = useState(false);
  const [importError, setImportError] = useState('');

  const clearAllScenes = useCallback(() => {
    setScenes([]);
    setSelectedIndex(-1);
    setActivatedSceneIds([]);
    setStorySummary('');
  }, []);

  const handleConfirmClearAll = useCallback(async () => {
    setIsClearingAll(true);
    try {
      clearAllScenes();
      setClearAllOpen(false);
    } finally {
      setIsClearingAll(false);
    }
  }, [clearAllScenes]);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [createSceneOpen, setCreateSceneOpen] = useState(false);
  const [isCreatingScene, setIsCreatingScene] = useState(false);

  const handleCreateScene = useCallback(({ sceneName, storyName, numScenes }) => {
    const userBase = (sceneName || '').trim();
    setScenes((prev) => {
      const baseIndex = prev.length;
      const title = numScenes === 1
        ? (userBase || `Scene ${baseIndex + 1}`)
        : `${userBase || 'Scene'}${baseIndex + 1}`;
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${baseIndex}`;
      const newScene = { id, title, storyName: storyName || '', sceneName: title };
      const next = [...prev, newScene];
      setSelectedIndex(baseIndex);
      setActivatedSceneIds((prevIds) => (prevIds.includes(newScene.id) ? prevIds : [...prevIds, newScene.id]));
      return next;
    });
    // setCreateSceneOpen(false);
    return;
    setIsCreatingScene(true);
    try {
      // Check if story already exists in backend before creating
      // const baseUrl = getServerURL();
      // const storyTrimmed = (storyName || '').trim();
      // if (storyTrimmed) {
      //   try {
      //     const params = new URLSearchParams({ limit: '1', story: storyTrimmed });
      //     const resp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
      //       headers: authService.getAuthHeaders(),
      //     });
      //     if (resp.ok) {
      //       const json = await resp.json();
      //       const exists = Array.isArray(json?.data) && json.data.length > 0;
      //       if (exists) {
      //         if (typeof setGlobalToastMessage === 'function' && typeof setGlobalToastActive === 'function') {
      //           setGlobalToastMessage('Error: story already exists');
      //           setGlobalToastActive(true);
      //           setTimeout(() => setGlobalToastActive(false), 2000);
      //         }
      //         return;
      //       }
      //     }
      //   } catch (_e) {}
      // }

      const count = Math.max(1, Number(numScenes) || 1);
      setScenes((prev) => {
        const baseIndex = prev.length;
        const additions = Array.from({ length: count }, (_, i) => {
          const title = count === 1
            ? ((sceneName || '').trim() || `Scene ${baseIndex + 1}`)
            : `${(sceneName || '').trim() || 'Scene'}${i + 1}`;
          const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${i}`;
          return { id, title, storyName: storyName || '', sceneName: title };
        });
        const next = [...prev, ...additions];
        setSelectedIndex(baseIndex);
        setActivatedSceneIds((prevIds) => (prevIds.includes(additions[0].id) ? prevIds : [...prevIds, additions[0].id]));
        return next;
      });
      setCreateSceneOpen(false);
    } finally {
      setIsCreatingScene(false);
    }
  }, [scenes]);

  const openCreateSceneModal = useCallback(() => {
    // setCreateSceneOpen(true);
    console.log('openCreateSceneModal', fetchStoryName, scenes);
    // return;
    handleCreateScene({ sceneName: `${scenes.length + 1}`, storyName: fetchStoryName, numScenes: 1 });
  }, [fetchStoryName, scenes.length, handleCreateScene]);

  const requestDelete = useCallback((index) => {
    setPendingDeleteIndex(index);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (pendingDeleteIndex == null) return;
    setIsDeleting(true);
    try {
      setScenes((prev) => {
        const copy = [...prev];
        const removed = copy.splice(pendingDeleteIndex, 1)[0];
        // Update activated set
        setActivatedSceneIds((prevIds) => prevIds.filter((id) => id !== removed?.id));
        // Update selected index based on the resulting list
        setSelectedIndex((prevSel) => {
          if (copy.length === 0) return -1;
          if (prevSel === pendingDeleteIndex) return Math.max(0, prevSel - 1);
          if (prevSel > pendingDeleteIndex) return prevSel - 1;
          return prevSel;
        });
        return copy;
      });
      setDeleteModalOpen(false);
      setPendingDeleteIndex(null);
    } finally {
      setIsDeleting(false);
    }
  }, [pendingDeleteIndex]);

  // When selection changes, activate that scene instance if not yet activated
  useEffect(() => {
    if (selectedIndex < 0 || selectedIndex >= scenes.length) return;
    const id = scenes[selectedIndex]?.id;
    if (!id) return;
    setActivatedSceneIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, [selectedIndex, scenes]);

  // Drag and drop handlers for reordering scenes (frontend only)
  const handleDragStart = useCallback((index) => (event) => {
    try {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    } catch (_e) {}
    setDraggingIndex(index);
  }, []);

  const handleDragOver = useCallback((index) => (event) => {
    event.preventDefault();
    try { event.dataTransfer.dropEffect = 'move'; } catch (_e) {}
    if (dragOverIndex !== index) {
      setDragOverIndex(index);
    }
  }, [dragOverIndex]);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((index) => (event) => {
    event.preventDefault();
    let fromIndex = draggingIndex;
    if (fromIndex == null) {
      try {
        const dt = event.dataTransfer.getData('text/plain');
        const parsed = Number(dt);
        if (!Number.isNaN(parsed)) fromIndex = parsed;
      } catch (_e) {}
    }
    setDragOverIndex(null);
    setDraggingIndex(null);
    if (fromIndex == null || fromIndex === index) return;

    setScenes((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(index, 0, moved);
      return next;
    });

    setSelectedIndex((prevSel) => {
      if (prevSel === fromIndex) return index;
      // If the selected item is between the move range, adjust
      if (fromIndex < prevSel && index >= prevSel) return prevSel - 1;
      if (fromIndex > prevSel && index <= prevSel) return prevSel + 1;
      return prevSel;
    });
  }, [draggingIndex]);

  const handleDragEnd = useCallback(() => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleFetchStoryScenes = useCallback(async () => {
    const story = (fetchStoryName || '').trim();
    setFetchError('');
    if (!story) {
      setFetchError('Please enter a story name.');
      return;
    }
    try {
      setIsFetchingStoryScenes(true);
      const baseUrl = getServerURL();
      const params = new URLSearchParams({ limit: '50', story });
      const resp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
        headers: authService.getAuthHeaders(),
      });
      if (!resp.ok) {
        setFetchError('Failed to fetch story generations.');
        return;
      }
      const json = await resp.json();
      const items = Array.isArray(json?.data) ? json.data : [];
      // Extract unique scenes with latest data in insertion order
      const seen = new Set();
      const sceneRecords = [];
      for (const item of items) {
        const sceneVal = item?.scene || item?.data?.scene || item?.sceneName || item?.data?.sceneName || '';
        const cleanScene = typeof sceneVal === 'string' ? sceneVal.trim() : '';
        if (!cleanScene || seen.has(cleanScene)) continue;
        seen.add(cleanScene);
        const prompt = item?.prompt || item?.data?.prompt || '';
        const outputImageUrl = item?.outputImageUrl || item?.imageUrl || item?.data?.outputImageUrl || '';
        const sceneText = item?.scene_text || item?.data?.scene_text || '';
        const rawType = item?.type || item?.action || item?.designType || item?.data?.type || '';
        const previewType = typeof rawType === 'string' ? rawType.toLowerCase() : '';
        const expandDirection = (item?.expand_direction || item?.expandDirection || item?.data?.expand_direction || '').toLowerCase();
        const designId = item?._id || item?.id || item?.data?._id || item?.data?.id || '';
        sceneRecords.push({ sceneName: cleanScene, prompt, outputImageUrl, sceneText, previewType, expandDirection, designId });
      }

      if (sceneRecords.length === 0) {
        setFetchError('No scenes found for this story.');
        return;
      }

      const now = Date.now();
      // Sort scenes by name (natural, case-insensitive)
      const orderedRecords = [...sceneRecords].sort((a, b) =>
        String(a.sceneName || '').localeCompare(String(b.sceneName || ''), undefined, { numeric: true, sensitivity: 'base' })
      );
      const additions = orderedRecords.map((rec, i) => ({
        id: `${now}_${Math.random().toString(36).slice(2, 8)}_${i}`,
        title: rec.sceneName,
        storyName: story,
        sceneName: rec.sceneName,
        initialPrompt: rec.prompt,
        initialOutputImageUrl: rec.outputImageUrl,
        sceneText: rec.sceneText || '',
        initialPreviewType: rec.previewType,
        initialExpandDirection: rec.expandDirection,
        initialDesignId: rec.designId || '',
      }));

      setScenes(additions);
      setSelectedIndex(0);
      setActivatedSceneIds([additions[0].id]);
    } catch (_e) {
      setFetchError('Unexpected error while fetching scenes.');
    } finally {
      setIsFetchingStoryScenes(false);
    }
  }, [fetchStoryName]);

  // Refs for child scenes to trigger generate
  const sceneRefs = React.useRef({});
  const setSceneRef = useCallback((id, node) => {
    if (!sceneRefs.current) sceneRefs.current = {};
    if (node) {
      sceneRefs.current[id] = node;
    } else {
      delete sceneRefs.current[id];
    }
  }, []);

  const [isSavingSceneText, setIsSavingSceneText] = useState(false);
  const handleSaveSceneText = useCallback(async () => {
    if (isSavingSceneText) return;
    const current = scenes[selectedIndex];
    const ref = current ? sceneRefs.current?.[current.id] : null;
    const designId = ref && typeof ref.getSelectedDesignId === 'function' ? ref.getSelectedDesignId() : '';
    if (!designId) return;
    try {
      setIsSavingSceneText(true);
      const baseUrl = getServerURL();
      await fetch(`${baseUrl}/design/edit/${designId}`, {
        method: 'PUT',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ scene_text: (current?.sceneText || '') }),
      });
      if (typeof setGlobalToastMessage === 'function' && typeof setGlobalToastActive === 'function') {
        setGlobalToastMessage('Scene text saved');
        setGlobalToastActive(true);
        setTimeout(() => setGlobalToastActive(false), 2000);
      }
    } catch (_e) {
    } finally {
      setIsSavingSceneText(false);
    }
  }, [isSavingSceneText, selectedIndex, scenes]);

  const handleGenerateAll = useCallback(async () => {
    if (isGeneratingAll) return;
    try {
      setIsGeneratingAll(true);
      // Activate all scenes so refs mount
      const allIds = scenes.map((s) => s.id);
      setActivatedSceneIds((prev) => Array.from(new Set([...(prev || []), ...allIds])));

      // Wait for next tick to ensure refs are attached
      await new Promise((resolve) => setTimeout(resolve, 0));

      const tasks = scenes.map((scene) => {
        const ref = sceneRefs.current?.[scene.id];
        if (ref && typeof ref.generate === 'function') {
          return ref.generate();
        }
        return Promise.resolve();
      });

      await Promise.allSettled(tasks);
    } finally {
      setIsGeneratingAll(false);
    }
  }, [isGeneratingAll, scenes]);

  const handleExpandAll = useCallback(async () => {
    if (isExpandingAll) return;
    try {
      setIsExpandingAll(true);
      // Ensure auth headers are initialized (in case of fresh tab or reload)
      try { authService.initializeAuth?.(); } catch (_e) {}

      // Ensure all scenes are activated so refs mount
      const allIds = scenes.map((s) => s.id);
      setActivatedSceneIds((prev) => Array.from(new Set([...(prev || []), ...allIds])));
      // Give React time to mount children and attach refs
      await new Promise((resolve) => setTimeout(resolve, 75));

      // Trigger expand on each scene in parallel, ensuring required fields per scene
      const tasks = scenes.map((scene) => (async () => {
        // Retry a couple of times in case refs are not ready yet
        let ref = sceneRefs.current?.[scene.id];
        for (let attempt = 0; attempt < 3 && (!ref || typeof ref.expand !== 'function'); attempt++) {
          await new Promise((r) => setTimeout(r, 50));
          ref = sceneRefs.current?.[scene.id];
        }
        if (ref && typeof ref.expand === 'function') {
          const inputUrl = (typeof ref.getInputUrl === 'function' ? (ref.getInputUrl() || '') : '').trim();
          const previewUrl = (typeof ref.getPreviewUrl === 'function' ? (ref.getPreviewUrl() || '') : '').trim();
          const imageUrl = inputUrl || previewUrl;
          const prompt = typeof ref.getPrompt === 'function' ? (ref.getPrompt() || '') : '';
          const expandDirection = typeof ref.getExpandDirection === 'function' ? (ref.getExpandDirection() || 'left') : 'left';
          if (!imageUrl || !prompt || !expandDirection) return;
          try {
            await ref.expand({ sceneText: scene.sceneText || '' });
          } catch (_e) {}
        }
      })());

      await Promise.allSettled(tasks);
    } finally {
      setIsExpandingAll(false);
    }
  }, [isExpandingAll, scenes]);

  const handleUpscaleAll = useCallback(async () => {
    if (isUpscalingAll) return;
    try {
      setIsUpscalingAll(true);
      // Ensure all scenes are activated so refs mount
      const allIds = scenes.map((s) => s.id);
      setActivatedSceneIds((prev) => Array.from(new Set([...(prev || []), ...allIds])));
      await new Promise((resolve) => setTimeout(resolve, 0));

      const baseUrl = getServerURL();
      const tasks = scenes.map(async (scene) => {
        const ref = sceneRefs.current?.[scene.id];
        if (ref && typeof ref.upscale === 'function') {
          // Use the component's own upscale to trigger loaders and overlay
          try {
            await ref.upscale();
          } catch (_e) {}
          return;
        }
        // Fallback: direct request (no internal loaders)
        const inputUrl = typeof ref?.getInputUrl === 'function' ? (ref.getInputUrl() || '').trim() : '';
        const previewUrl = typeof ref?.getPreviewUrl === 'function' ? (ref.getPreviewUrl() || '').trim() : '';
        const imageUrl = inputUrl || previewUrl;
        if (!imageUrl) return;
        const model = typeof ref?.getUpscaleModel === 'function' ? (ref.getUpscaleModel() || 'anime') : 'anime';
        try {
          const resp = await fetch(`${baseUrl}/design/upscale`, {
            method: 'POST',
            headers: {
              ...authService.getAuthHeaders(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              imageUrl,
              upscale_factor: 1,
              model,
              story: scene.storyName || '',
              scene: scene.sceneName || scene.title || '',
              scene_text: scene.sceneText || '',
            }),
          });
          try {
            const data = await resp.clone().json();
            const candidates = [data?.data, data?.result, data?.design, data?.data?.design, data];
            let outputUrl = '';
            let designId = '';
            for (const c of candidates) {
              if (!c || typeof c !== 'object') continue;
              if (!outputUrl) outputUrl = c.imageUrl || c.outputImageUrl || '';
              if (!designId) designId = c._id || c.id || c.designId || c.design_id || '';
              if (outputUrl && designId) break;
            }
            if (outputUrl && typeof ref?.setPreviewFromUpscale === 'function') {
              ref.setPreviewFromUpscale({ inputUrl: imageUrl, outputUrl, designId });
            }
          } catch (_ignore) {}
        } catch (_e) {}
      });
      await Promise.allSettled(tasks);
      if (typeof setGlobalToastMessage === 'function' && typeof setGlobalToastActive === 'function') {
        setGlobalToastMessage('Requested upscale for all scenes');
        setGlobalToastActive(true);
        setTimeout(() => setGlobalToastActive(false), 2000);
      }
    } finally {
      setIsUpscalingAll(false);
    }
  }, [isUpscalingAll, scenes]);

  const handleDownloadAll = useCallback(async () => {
    if (isDownloadingAll) return;
    setIsDownloadingAll(true);
    try {
      // Ensure all scenes are activated/mounted so refs are present
      const allIds = scenes.map((s) => s.id);
      setActivatedSceneIds((prev) => Array.from(new Set([...(prev || []), ...allIds])));
      await new Promise((r) => setTimeout(r, 0));

      const baseUrl = getServerURL();

      // Collect blobs in parallel; fallback to API lookup if preview URL missing
      const downloads = await Promise.allSettled(
        scenes.map(async (scene) => {
          const ref = sceneRefs.current?.[scene.id];
          let url = ref && typeof ref.getPreviewUrl === 'function' ? ref.getPreviewUrl() : '';
          if (!url && scene.storyName && scene.sceneName) {
            try {
              const params = new URLSearchParams({ limit: '1', story: scene.storyName, scene: scene.sceneName });
              const recResp = await fetch(`${baseUrl}/design/recent?${params.toString()}`, {
                headers: authService.getAuthHeaders(),
              });
              if (recResp.ok) {
                const recJson = await recResp.json();
                const item = Array.isArray(recJson?.data) ? recJson.data[0] : null;
                url = item?.outputImageUrl || item?.imageUrl || item?.data?.outputImageUrl || '';
              }
            } catch (_e) {}
          }
          if (!url) return null;
          try {
            const resp = await fetch(url, { mode: 'cors' });
            if (!resp.ok) return null;
            const blob = await resp.blob();
            let fileName = (scene.sceneName || scene.title || 'image').trim();
            if (!/\.(png|jpg|jpeg|webp|gif)$/i.test(fileName)) fileName = `${fileName}.png`;
            return { fileName, blob };
          } catch (_e) {
            return null;
          }
        })
      );

      const zip = new JSZip();
      const usedNames = new Map();
      for (const r of downloads) {
        if (r.status === 'fulfilled' && r.value) {
          let name = r.value.fileName;
          // Ensure unique filenames inside the zip
          const dot = name.lastIndexOf('.');
          const base = dot > 0 ? name.slice(0, dot) : name;
          const ext = dot > 0 ? name.slice(dot) : '';
          const count = (usedNames.get(name) || 0) + 1;
          usedNames.set(name, count);
          if (count > 1) {
            name = `${base}_${count}${ext}`;
          }
          zip.file(name, r.value.blob);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      const url = URL.createObjectURL(zipBlob);
      a.href = url;
      const story = scenes[0]?.storyName || 'story';
      a.download = `${story}_${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingAll(false);
    }
  }, [isDownloadingAll, scenes]);

  const activeStoryName = scenes.length > 0 ? (scenes[0]?.storyName || '') : (fetchStoryName || '');

  const openImportModal = useCallback(() => {
    setImportError('');
    setImportStoryName(activeStoryName || '');
    setImportModalOpen(true);
  }, [activeStoryName]);

  const openTellStoryModal = useCallback(() => {
    setTellError('');
    setTellKidGender('');
    setTellKidAge('');
    setTellHero('');
    setTellScenes('8');
    setTellStoryName('');
    setTellKidName('');
    setTellStoryOpen(true);
  }, []);

  const handleTellStoryGenerate = useCallback(async () => {
    setTellError('');
    const gender = (tellKidGender || '').trim();
    const ageParsed = parseInt(tellKidAge || '', 10);
    const scenesParsed = parseInt(tellScenes || '', 10);
    if (!gender || Number.isNaN(ageParsed) || !(tellHero || '').trim() || !(tellStoryName || '').trim() || !(tellKidName || '').trim()) {
      setTellError('Please fill in all fields.');
      return;
    }
    const ageVal = Math.min(15, Math.max(1, ageParsed));
    const scenesVal = scenesParsed;
    const payload = {
      kid_gender: gender,
      kid_age: String(ageVal),
      hero: (tellHero || '').trim(),
      scenes: scenesVal,
      story_name: (tellStoryName || '').trim(),
      kid_name: (tellKidName || '').trim(),
      ai_generated: tellAIGenerated,
    };
    try {
      setIsGeneratingStoryScenes(true);
      const baseUrl = getServerURL();
      const resp = await fetch(`${baseUrl}/generate-story-scenes`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const clone = resp.clone();
      let json = null;
      try {
        json = await resp.json();
      } catch (_e) {}
      if (!resp.ok) {
        let serverMessage = json?.message || json?.error || json?.errors?.[0]?.message || '';
        if (!serverMessage) {
          try {
            const asText = await clone.text();
            serverMessage = (asText || '').slice(0, 500);
          } catch (_e2) {}
        }
        setTellError(serverMessage || 'Failed to request story generation.');
        return;
      }
      if (!json) {
        setTellError('Unexpected empty response from server.');
        return;
      }
      const data = json?.data || null;
      if (!json?.success || !data) {
        const serverMessage = json?.message || json?.error || json?.errors?.[0]?.message || 'Unexpected response from server.';
        setTellError(serverMessage);
        return;
      }
      const name = (data?.input?.story_name || payload.story_name || '').trim();
      const sceneList = Array.isArray(data?.scenes) ? data.scenes : [];
      if (sceneList.length === 0) {
        setTellError('No scenes returned.');
        return;
      }
      const now = Date.now();
      const additions = sceneList.map((s, i) => {
        const sceneNumber = s?.scene_number != null ? String(s.scene_number) : String(i + 1);
        return {
          id: `${now}_${Math.random().toString(36).slice(2, 8)}_${i}`,
          title: sceneNumber,
          storyName: name,
          sceneName: sceneNumber,
          initialPrompt: s?.scene_prompt || '',
          sceneText: s?.scene_text || '',
          sceneCharacters: s?.scene_characters || '',
        };
      });
      setScenes(additions);
      setSelectedIndex(0);
      setActivatedSceneIds([additions[0].id]);
      setStorySummary(data?.summary || '');
      setTellStoryOpen(false);
      if (typeof setGlobalToastMessage === 'function' && typeof setGlobalToastActive === 'function') {
        setGlobalToastMessage(`Created ${additions.length} scenes`);
        setGlobalToastActive(true);
        setTimeout(() => setGlobalToastActive(false), 2000);
      }
    } catch (_e) {
      setTellError('Unexpected error while generating story.');
    } finally {
      setIsGeneratingStoryScenes(false);
    }
  }, [tellKidGender, tellKidAge, tellHero, tellScenes, tellStoryName, tellKidName]);

  const handleConfirmImport = useCallback(async () => {
    const name = (importStoryName || '').trim();
    setImportError('');
    if (!name) {
      setImportError('Please enter a story name.');
      return;
    }
    try {
      setIsImportingStory(true);
      const baseUrl = process.env.REACT_APP_SERVER_URL;
      const params = new URLSearchParams({ story_name: name });
      const resp = await fetch(`${baseUrl}/design/creation-stories?${params.toString()}`, {
        headers: authService.getAuthHeaders(),
      });
      if (!resp.ok) {
        setImportError('Failed to fetch story creation scenes.');
        return;
      }
      const json = await resp.json();
      const items = Array.isArray(json?.data) ? json.data : [];
      if (items.length === 0) {
        setImportError('No story found.');
        return;
      }
      const record = items.find((r) => (r?.input?.story_name || '').toLowerCase() === name.toLowerCase()) || items[0];
      const sceneList = Array.isArray(record?.scenes) ? record.scenes : [];
      if (sceneList.length === 0) {
        setImportError('No scenes available in the story.');
        return;
      }
      const now = Date.now();
      const additions = sceneList.map((s, i) => {
        const sceneNumber = s?.scene_number != null ? String(s.scene_number) : String(i + 1);
        return {
          id: `${now}_${Math.random().toString(36).slice(2, 8)}_${i}`,
          title: sceneNumber,
          storyName: name,
          sceneName: sceneNumber,
          initialPrompt: s?.scene_prompt || '',
          sceneText: s?.scene_text || '',
          sceneCharacters: s?.scene_characters || '',
        };
      });
      setScenes(additions);
      setSelectedIndex(0);
      setActivatedSceneIds([additions[0].id]);
      setStorySummary(record?.summary || '');
      setImportModalOpen(false);
      if (typeof setGlobalToastMessage === 'function' && typeof setGlobalToastActive === 'function') {
        setGlobalToastMessage(`Imported ${additions.length} scenes`);
        setGlobalToastActive(true);
        setTimeout(() => setGlobalToastActive(false), 2000);
      }
    } catch (_e) {
      setImportError('Unexpected error while importing scenes.');
    } finally {
      setIsImportingStory(false);
    }
  }, [importStoryName]);

  return (
    <div>
      <style>
        {`
        @font-face {
          font-family: 'PingLCG';
          src: url('https://snapsai.blob.core.windows.net/input/fonts/PingLCG-Regular.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        @font-face {
          font-family: 'PingARLT';
          src: url('https://snapsai.blob.core.windows.net/input/fonts/PingAR+LT-Regular.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        .NoMarginButtonRow .Polaris-Button.Polaris-Button--pressable.Polaris-Button--sizeMedium.Polaris-Button--textAlignCenter {
          margin: 0 !important;
          min-width: 95px;
        }
        .NoMarginButtonRow .Polaris-Text--root.Polaris-Text--bodySm.Polaris-Text--medium {
          display: flex;
          align-items: center;
        }
        /* Scoped margin for top-right Import and Tell Me A Story buttons only */
        .TopRightScopedButtons button.Polaris-Button.Polaris-Button--pressable.Polaris-Button--sizeMedium.Polaris-Button--textAlignCenter.Polaris-Button--iconWithText {
          margin: 16px 0;
        }
        `}
      </style>
      <Layout style={{ marginBottom: '24px' }}>
      <Layout.Section>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              {activeStoryName ? (
                storySummary ? (
                  <Tooltip content={storySummary} preferredPosition="below">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'help' }}>
                      <Text as="h2" variant="headingSm">Story Title : {activeStoryName}</Text>
                      <Icon source={CircleInformationMajor} tone="base" />
                    </div>
                  </Tooltip>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Text as="h2" variant="headingSm">Story Title : {activeStoryName}</Text>
                    <Icon source={CircleInformationMajor} tone="base" />
                  </div>
                )
              ) : null}
            </div>
            <InlineStack align="end" gap="200">
              <div className="TopRightScopedButtons">
                <Button icon={IncomingMajor} variant="secondary" size="medium" onClick={openImportModal}>
                  Import
                </Button>
              </div>
              <div className="TopRightScopedButtons">
                <Button icon={ChatMajor} variant="primary" size="medium" onClick={openTellStoryModal}>
                  Tell Me A Story
                </Button>
              </div>
              <div className="TopRightScopedButtons">
                <Button icon={AddMajor} onClick={openCreateSceneModal} variant="primary" size="medium">
                  Add New Scene
                </Button>
              </div>
            </InlineStack>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, minHeight: '70vh' }}>
          {/* Left fixed vertical tabs */}
          <div
            style={{
              width: 130,
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              padding: 12,
              position: 'sticky',
              top: 0,
              height: 'fit-content',
              alignSelf: 'flex-start',
              background: 'white',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text as="h2" variant="headingMd">Scenes</Text>
              <Text tone="subdued" variant="bodySm">{scenes.length}</Text>
            </div>

            {scenes.length === 0 ? (
              <div
                aria-disabled
                style={{
                  opacity: 0.6,
                  border: '1px dashed #E5E7EB',
                  borderRadius: 8,
                  padding: '12px 10px',
                  textAlign: 'center',
                  color: '#6B7280',
                }}
              >
                No scenes yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {scenes.map((scene, index) => {
                  const isSelected = index === selectedIndex;
                  return (
                    <div
                      key={scene.id}
                      draggable
                      onDragStart={handleDragStart(index)}
                      onDragOver={handleDragOver(index)}
                      onDrop={handleDrop(index)}
                      onDragLeave={handleDragLeave}
                      onDragEnd={handleDragEnd}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 12px',
                        borderRadius: 8,
                        background: isSelected ? '#F3F4F6' : dragOverIndex === index ? '#F9FAFB' : 'transparent',
                        border: `1px solid ${isSelected ? '#D1D5DB' : dragOverIndex === index ? '#E5E7EB' : 'transparent'}`,
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={scene.title}>
                        {scene.title}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); requestDelete(index); }}
                        aria-label={`Delete ${scene.title}`}
                        title={`Delete ${scene.title}`}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#6B7280',
                          cursor: 'pointer',
                          fontSize: 16,
                          lineHeight: 1,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ height: 12 }} />
            <div style={{ borderTop: '1px solid #E5E7EB', margin: '8px 0' }} />
            <Button variant="primary" icon={MagicMinor} onClick={handleGenerateAll} loading={isGeneratingAll} disabled={scenes.length === 0}>
              Generate All Scenes
            </Button>
            <div style={{ height: 8 }} />
            <div className="NoMarginButtonRow">
              <Button variant="primary" icon={ExploreImagesMajor} onClick={handleUpscaleAll} loading={isUpscalingAll} disabled={scenes.length === 0} style={{ margin: 0, backgroundColor: '#2563EB', borderColor: '#2563EB', color: '#FFFFFF' }}>
                Upscale All Scenes
              </Button>
            </div>
            <div style={{ height: 8 }} />
            <div className="NoMarginButtonRow">
              <Button variant="secondary" icon={MaximizeMinor} onClick={handleExpandAll} loading={isExpandingAll} disabled={scenes.length === 0} style={{ margin: 0, backgroundColor: '#10B981', borderColor: '#10B981', color: '#FFFFFF' }}>
                Expand All Scenes
              </Button>
            </div>
            <div style={{ height: 8 }} />
            <div className="NoMarginButtonRow">
              <Button icon={ImportMinor} onClick={handleDownloadAll} loading={isDownloadingAll} disabled={scenes.length === 0} style={{ margin: 0 }}>
                Download All Scenes
              </Button>
            </div>
            <div style={{ height: 4 }} />
            <div style={{ height: 8 }} />
            <Button tone="critical" variant="primary" size="slim" fullWidth icon={CancelSmallMinor} onClick={() => setClearAllOpen(true)} disabled={scenes.length === 0}>
              Clear all
            </Button>
          </div>

          {/* Right content */}
          <div style={{ flex: 1 }}>
            {scenes.length === 0 ? (
              <div
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: 12,
                  minHeight: '60vh',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#374151',
                  padding: 24,
                }}
              >
                <div style={{ width: 360, maxWidth: '90%' }}>
                  <TextField
                    label="Fetch Story Generations"
                    value={fetchStoryName}
                    onChange={setFetchStoryName}
                    placeholder="Enter story name (e.g., Nemo)"
                    autoComplete="off"
                  />
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                    <Button onClick={handleFetchStoryScenes} variant="primary" loading={isFetchingStoryScenes}>
                      Fetch
                    </Button>
                  </div>
                  {fetchError ? (
                    <div style={{ marginTop: 8 }}>
                      <Text tone="critical" variant="bodySm">{fetchError}</Text>
                    </div>
                  ) : null}
                </div>
                <div style={{ color: '#6B7280', marginTop: 8 }}>or use "Add New Scene" to create manually</div>
              </div>
            ) : (
              scenes.map((scene, index) => (
                activatedSceneIds.includes(scene.id) ? (
                  <div key={scene.id} style={{ display: index === selectedIndex ? 'block' : 'none' }}>
                      <AIDesignImaginePage
                      ref={(node) => setSceneRef(scene.id, node)}
                      embedded
                      sceneIndex={index}
                      storyName={scene.storyName || ''}
                      sceneName={scene.sceneName || scene.title || ''}
                      initialPrompt={scene.initialPrompt || ''}
                      initialOutputImageUrl={scene.initialOutputImageUrl || ''}
                        initialExpandDirection={scene.initialExpandDirection || ''}
                        initialPreviewType={scene.initialPreviewType || ''}
                        initialDesignId={scene.initialDesignId || ''}
                      enableUpscaleControls={true}
                      enableExpandControls
                      recentType={''}
                      sceneText={scene.sceneText || ''}
                      sceneCharacters={scene.sceneCharacters || ''}
                      onSceneTextChange={(val) => setScenes((prev) => prev.map((s, i) => i === index ? { ...s, sceneText: val } : s))}
                      setToastMessage={setGlobalToastMessage}
                      setToastActive={setGlobalToastActive}
                      renderActions={() => (
                        <Button icon={SaveMinor} onClick={handleSaveSceneText} loading={isSavingSceneText}>
                          Save
                        </Button>
                      )}
                      renderOverlay={({ previewType, expandDirection, imageRect }) => (
                        previewType === 'expand' && (scene.sceneText || '').trim() ? (
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              pointerEvents: 'none',
                              zIndex: 999,
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                left: (String(expandDirection || '').toLowerCase() === 'right')
                                  ? (imageRect.left + imageRect.width / 2)
                                  : imageRect.left,
                                top: imageRect.top,
                                width: imageRect.width / 2,
                                height: imageRect.height,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 50,
                              }}
                            >
                              <div
                                style={{
                                  fontFamily: (/[ -]|/.test('') ? '' : '') || (/[ -]/.test('') ? '' : ''), // no-op placeholder to satisfy formatter
                                  fontFamily: (/[\u0600-\u06FF]/.test(scene.sceneText || ''))
                                    ? "'PingARLT', Arial, sans-serif"
                                    : "'PingLCG', 'Arial Black', Arial, sans-serif",
                                  fontSize: 22,
                                  fontWeight: 'bold',
                                  color: '#FFFFFF',
                                  textAlign: 'center',
                                  textShadow: '0 0 3px rgba(0,0,0,0.9), 2px 2px 4px rgba(0,0,0,0.85)',
                                  lineHeight: 1.5,
                                  maxWidth: 'calc(100% - 100px)',
                                  wordWrap: 'break-word',
                                }}
                              >
                                {scene.sceneText}
                              </div>
                            </div>
                          </div>
                        ) : null
                      )}
                    />
                  </div>
                ) : null
              ))
            )}
          </div>
        </div>

        <DeleteSceneModal
          isOpen={deleteModalOpen}
          onClose={() => { setDeleteModalOpen(false); setPendingDeleteIndex(null); }}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeleting}
        />

        <CreateSceneModal
          isOpen={createSceneOpen}
          onClose={() => setCreateSceneOpen(false)}
          onCreateScene={handleCreateScene}
          isCreating={isCreatingScene}
        />

        <ConfirmAIStoryActionModal
          isOpen={clearAllOpen}
          onClose={() => setClearAllOpen(false)}
          onConfirm={handleConfirmClearAll}
          loading={isClearingAll}
          destructive
          title="Clear all"
          message="This will remove all scenes from the page. This action is frontend-only and will not affect backend data."
          confirmText="Clear"
          cancelText="Cancel"
        />

        {/* Import story modal */}
        <Modal
          open={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          title={<div style={{ padding: '16px' }}>{'Import Story Scenes'}</div>}
          primaryAction={{
            content: 'Import',
            onAction: handleConfirmImport,
            loading: isImportingStory,
            disabled: !(importStoryName || '').trim(),
          }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setImportModalOpen(false), disabled: isImportingStory }]}
          footer={<div style={{ padding: '16px' }}></div>}
        >
          <BlockStack gap="400">
            <TextField
              label="Story Name"
              value={importStoryName}
              onChange={setImportStoryName}
              placeholder="Enter story name (e.g., frozen00)"
              autoComplete="off"
              helpText="Enter the story name to import its scenes"
              disabled={isImportingStory}
            />
            {importError ? <Text tone="critical" variant="bodySm">{importError}</Text> : null}
          </BlockStack>
        </Modal>

        {/* Tell Me A Story modal */}
        <Modal
          open={tellStoryOpen}
          onClose={() => setTellStoryOpen(false)}
          title={<div style={{ padding: '16px' }}>{'Tell Me A Story'}</div>}
          primaryAction={{
            content: 'Generate',
            onAction: handleTellStoryGenerate,
            loading: isGeneratingStoryScenes,
          }}
          secondaryActions={[{ content: 'Cancel', onAction: () => setTellStoryOpen(false), disabled: isGeneratingStoryScenes }]}
          footer={<div style={{ padding: '16px' }}></div>}
        >
          <BlockStack gap="400">
            <Select
              label="Kid Gender"
              options={[
                { label: 'Select gender', value: '' },
                { label: 'Girl', value: 'girl' },
                { label: 'Boy', value: 'boy' },
              ]}
              value={tellKidGender}
              onChange={setTellKidGender}
              disabled={isGeneratingStoryScenes}
            />
            <TextField label="Kid Age" type="number" min={1} max={15} value={tellKidAge} onChange={setTellKidAge} autoComplete="off" placeholder="e.g., 4" disabled={isGeneratingStoryScenes} />
            <TextField label="Hero" value={tellHero} onChange={setTellHero} autoComplete="off" placeholder="e.g., Animals" disabled={isGeneratingStoryScenes} />
            <TextField label="Scenes" type="number" value={tellScenes} onChange={setTellScenes} autoComplete="off" placeholder="e.g., 8" disabled={isGeneratingStoryScenes} />
            <TextField label="Story Name" value={tellStoryName} onChange={setTellStoryName} autoComplete="off" placeholder="e.g., girl-and-animals" disabled={isGeneratingStoryScenes} />
            <TextField label="Kid Name" value={tellKidName} onChange={setTellKidName} autoComplete="off" placeholder="e.g., Mira" disabled={isGeneratingStoryScenes} />
            <Checkbox
              label="AI Generated"
              checked={tellAIGenerated}
              onChange={setTellAIGenerated}
              disabled={isGeneratingStoryScenes}
            />
            {tellError ? <Text tone="critical" variant="bodySm">{tellError}</Text> : null}
          </BlockStack>
        </Modal>
      </Layout.Section>
    </Layout>
    </div>
  );
}

export default AIStoryPage;


